import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { DateTimePicker } from "@/components/DateTimePicker";

export const PulseTaskDialog = ({ open, onOpenChange, taskForm, setTaskForm, onSave, onDelete, editingTask }) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">
            {editingTask ? t("pulsePlanner.editTask") : t("pulsePlanner.newTask")}
          </DialogTitle>
          <DialogDescription>{t("pulsePlanner.addOrUpdateTask")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>{t("pulsePlanner.titleRequired")}</Label>
            <Input
              value={taskForm.title}
              onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
              placeholder={t("pulsePlanner.taskTitlePlaceholder")}
              data-testid="task-title-input"
              className="rounded-sm"
            />
          </div>
          <div>
            <Label>{t("pulsePlanner.description")}</Label>
            <Textarea
              value={taskForm.description}
              onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
              placeholder={t("pulsePlanner.taskDetailsPlaceholder")}
              rows={3}
              className="rounded-sm"
            />
          </div>
          <div>
            <Label className="mb-2 block">{t("pulsePlanner.dueDateTime")}</Label>
            <DateTimePicker
              value={taskForm.due_datetime}
              onChange={(value) => setTaskForm({ ...taskForm, due_datetime: value })}
              data-testid="task-due-input"
            />
          </div>
          <div>
            <Label>{t("pulsePlanner.priority")}</Label>
            <Select
              value={taskForm.priority}
              onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}
            >
              <SelectTrigger data-testid="task-priority-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />{t("pulsePlanner.low")}
                  </span>
                </SelectItem>
                <SelectItem value="medium">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />{t("pulsePlanner.medium")}
                  </span>
                </SelectItem>
                <SelectItem value="high">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500" />{t("pulsePlanner.high")}
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="show_on_calendar"
              checked={taskForm.show_on_calendar}
              onCheckedChange={(checked) => setTaskForm({ ...taskForm, show_on_calendar: checked })}
            />
            <Label htmlFor="show_on_calendar" className="cursor-pointer">
              {t("pulsePlanner.showOnCalendar")}
            </Label>
          </div>
        </div>
        <DialogFooter className="gap-2">
          {editingTask && (
            <Button variant="destructive" onClick={() => { onDelete(editingTask.id); onOpenChange(false); }}>
              {t("pulsePlanner.delete")}
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("pulsePlanner.cancel")}</Button>
          <Button onClick={onSave} data-testid="save-task-btn">
            {editingTask ? t("pulsePlanner.update") : t("pulsePlanner.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
